/**
 * "Feedback" tab on the right edge of every authenticated page — the
 * vertical side tab most feedback widgets use (Hotjar, Usersnap). It sits
 * at mid-height so the bottom-right corner belongs to the Ask ABHI
 * launcher alone, and slides out a little on hover.
 *
 * Hidden for unauthenticated users (auth pages don't need it).
 * Clicking opens FeedbackDialog.
 */

import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Box, Tooltip, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useState } from 'react';

import { useAuth } from '../../auth/useAuth';
import { FeedbackDialog } from './FeedbackDialog';

const GOLD = '#bb8c3f';
const INK = '#0f0a07';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

const MotionBox = motion.create(Box);

export function FeedbackButton() {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);

  if (!isAuthenticated) return null;

  return (
    <>
      <MotionBox
        initial={{ opacity: 0, x: 16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut', delay: 0.4 }}
        sx={{
          position: 'fixed',
          right: 0,
          top: '50%',
          translate: '0 -50%',
          zIndex: 1250,
        }}
      >
        <Tooltip title="Tell us what you think" placement="left" arrow>
          <Box
            role="button"
            onClick={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen(true);
              }
            }}
            tabIndex={0}
            aria-label="Send feedback"
            sx={{
              // Vertical tab hugging the edge: text reads bottom-to-top.
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: { xs: 0.6, sm: 0.75 },
              py: { xs: 1.25, sm: 1.6 },
              // Rounded on the side facing the page (left, after the rotation: right).
              borderRadius: '0 10px 10px 0',
              bgcolor: '#fdf9f0',
              color: INK,
              border: '1px solid rgba(187, 140, 63, 0.32)',
              borderLeft: 'none',
              boxShadow: '4px 0 18px rgba(142, 106, 42, 0.14)',
              cursor: 'pointer',
              userSelect: 'none',
              transition:
                'background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease, color 200ms ease, translate 200ms ease',
              '&:hover': {
                color: GOLD,
                bgcolor: '#fff',
                borderColor: 'rgba(187, 140, 63, 0.6)',
                boxShadow: '6px 0 22px rgba(187, 140, 63, 0.22)',
                translate: '-3px 0',
              },
              '&:focus-visible': {
                outline: 'none',
                borderColor: GOLD,
                boxShadow: '0 0 0 3px rgba(187, 140, 63, 0.25)',
              },
            }}
          >
            <ChatBubbleOutlineIcon
              sx={{ fontSize: { xs: 14, sm: 15 }, transform: 'rotate(90deg)' }}
            />
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: { xs: 11.5, sm: 12.5 },
                fontWeight: 600,
                letterSpacing: '0.06em',
                color: 'inherit',
              }}
            >
              Feedback
            </Typography>
          </Box>
        </Tooltip>
      </MotionBox>

      <FeedbackDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
